import React from 'react';
import { Provider } from 'react-redux';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  Experimental_CssVarsProvider as CssVarsProvider,
  experimental_extendTheme as extendTheme,
} from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StyledEngineProvider } from '@mui/material/styles';
import Hotjar from '@hotjar/browser';
import { ErrorBoundary } from 'react-error-boundary';

import './index.scss';
import './style.scss';
import App from './App';
import { setupStore } from 'shared/store/store';
import { ga4 } from 'shared/libs/gtm';
import config from 'shared/assets/config';
import { ACErrorBoundary } from 'shared/components/taa/ErrorBoundary';
import {
  installGlobalBrowserErrorHandlers,
  reportBrowserError,
} from 'shared/hooks/api/reportBrowserError';

installGlobalBrowserErrorHandlers();

const theme = extendTheme({
  typography: {
    fontFamily: 'Open Sans, sans-serif',
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 429,
      md: 760,
      lg: 1200,
      xl: 1536,
    },
  },
  cssVarPrefix: 'ac',
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          padding: '0 !important',
          borderColor: '#f7f9fc',
          '&:hover': {
            border: '#f7f9fc !important',
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        option: {
          '&:hover': {
            backgroundColor: '#382ebe !important',
            color: '#ffffff',
          },
        },
        inputRoot: {
          '&:hover': {
            borderColor: '2px red solid',
          },
        },
      },
    },
  },
});

// Load the Google analytic
ga4.initialize({ measurementId: config.measurementId });

// Load the hotjar
Hotjar.init(Number(config.hotjarSiteID), 6);

const handleRootBrowserError = (error: Error, info: { componentStack: string }) => {
  reportBrowserError(error, { componentStack: info.componentStack });
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});
const store = setupStore();
const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <React.StrictMode>
    <ErrorBoundary FallbackComponent={ACErrorBoundary} onError={handleRootBrowserError}>
      <QueryClientProvider client={queryClient}>
        <StyledEngineProvider injectFirst>
          <CssVarsProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <BrowserRouter>
                <Provider store={store}>
                  <App />
                </Provider>
              </BrowserRouter>
            </LocalizationProvider>
          </CssVarsProvider>
        </StyledEngineProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
